import { useEffect, useState } from 'react';
import type { SprayWindow } from '@greeniq/shared';
import { getSprayWindows } from '../api/weather.js';
import { DataCard } from './ui/data-card';
import { Badge } from './ui/badge';

interface Props {
  courseSlug: string;
}

function fmtRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const day = s.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const hm = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${hm(s)} – ${hm(e)}`;
}

export function SprayWindowCard({ courseSlug }: Props) {
  const [windows, setWindows] = useState<SprayWindow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getSprayWindows(courseSlug, 3);
        if (!cancelled) setWindows(resp.windows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  if (error) return null;
  if (!windows) return null;

  return (
    <DataCard
      className="mt-6"
      title="Spray windows"
      subtitle="Next 48h · wind ≤ 10 mph, temp 60–85°F, RH 30–85%, < 0.01″ rain in 6h"
    >
      {windows.length === 0 ? (
        <p className="text-sm text-fg-muted">
          No clean windows in the next 48 hours. Check the 10-day forecast below.
        </p>
      ) : (
        <ul className="space-y-2">
          {windows.map((w, i) => (
            <li
              key={`${w.startTime}-${w.endTime}`}
              className="flex flex-wrap items-center gap-3 rounded-sm border border-border bg-bg-elevated p-3"
            >
              <Badge variant={i === 0 ? 'default' : 'secondary'}>#{i + 1}</Badge>
              <div className="min-w-[180px] flex-1">
                <div className="text-sm font-semibold text-fg">
                  {fmtRange(w.startTime, w.endTime)}
                </div>
                <div className="mt-0.5 text-xs text-fg-secondary">
                  {w.durationHours}h · {w.meanTempF.toFixed(0)}°F avg · wind{' '}
                  {w.meanWindMph.toFixed(1)} mph · RH {w.meanRhPct}%
                </div>
              </div>
              <div className="text-xs text-fg-muted">score {w.score.toFixed(1)}</div>
            </li>
          ))}
        </ul>
      )}
    </DataCard>
  );
}
